/**
 * `AppIssueSourceCard` — the App Integrations tab's inbound issue-tracker
 * binding (#1558 Slice 1b, #1826). Screen A2 of the epic mockup.
 *
 * The app selects a workspace issue-tracker connection, binds one or more
 * of its projects, maps issue types to requirement/defect (Story/Task →
 * requirement, Bug → defect resolved as suggestions from the discovered
 * types), and picks which content fields feed the Proposer. No credentials
 * — the workspace connection owns those. A read-only issue browser (with a
 * project filter) shows the classified issues once bound; import is Slice 2.
 *
 * Form-based, matching the GitHub/Slack cards on this tab (the mockup's
 * chip-based inline editing is idealized; the tab's real ethos is a form +
 * Save per card). Gated app-side to the binding tier — a non-admin simply
 * gets a 403 from the mutating/discovery calls, surfaced inline.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Loader2, Check, RefreshCw } from 'lucide-react'
import { Button, Label, Badge, SearchableSelect } from '@/components/ui'
import {
  applicationsAPI,
  type AppIssueTrackerConnection,
  type IssueSourceProject,
  type IssueSourceIssueType,
  type IssueSourceField,
  type IssueSourceIssue,
  type ImportIssueRequest,
  type IssueImportStatus,
} from '@/lib/api'
import type { AppIssueSourceBinding } from '@/types'
import { ScopeFilterBuilder } from '@/components/ScopeFilterBuilder'
import { invalidateCoverage } from '@/lib/issueCoverage'

type Cls = 'requirement' | 'defect' | 'ignore'

/** Compact "x ago" for the last-synced line. */
function relTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'recently'
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  return `${Math.floor(s / 86400)} d ago`
}
const DEFAULT_CONTENT_FIELDS = ['summary', 'description']

/** Suggest a default classification for a discovered issue type by name
 * (AC#4 — resolved from discovered types at setup, stored by ID). */
function defaultClassFor(name: string): Cls {
  const n = name.trim().toLowerCase()
  if (['story', 'task', 'epic', 'requirement', 'user story'].includes(n)) return 'requirement'
  if (['bug', 'defect', 'incident'].includes(n)) return 'defect'
  return 'ignore'
}

function seedClassification(binding: AppIssueSourceBinding | null): Record<string, Cls> {
  const out: Record<string, Cls> = {}
  for (const id of binding?.requirementIssueTypeIds ?? []) out[id] = 'requirement'
  for (const id of binding?.defectIssueTypeIds ?? []) out[id] = 'defect'
  return out
}

function providerConfigValue(conn: AppIssueTrackerConnection | undefined, key: string): string {
  const v = conn?.providerConfig?.[key]
  return typeof v === 'string' ? v : ''
}

const PROVIDER_LABELS: Record<string, string> = { jira: 'Jira' }
function providerLabel(provider: string | undefined): string {
  return provider ? (PROVIDER_LABELS[provider] ?? provider) : 'connection'
}

export function AppIssueSourceCard({
  appId,
  initialBinding,
  connections,
  settingsUrl,
}: {
  appId: string
  initialBinding: AppIssueSourceBinding | null
  connections: AppIssueTrackerConnection[]
  /** Workspace-scoped Integrations settings URL, passed from the parent tab
   * so all three "Open Workspace Settings" links share one source (#2306).
   * The card used to hardcode a relative `../../settings/integrations` that
   * dropped the workspace and landed on the wrong page. */
  settingsUrl: string
}) {
  const queryClient = useQueryClient()
  const [binding, setBinding] = useState<AppIssueSourceBinding | null>(initialBinding)
  const [editing, setEditing] = useState<boolean>(!initialBinding)

  // Form state (seeded from the binding; the user mutates then Saves).
  const [connectionId, setConnectionId] = useState(initialBinding?.connectionId ?? connections[0]?.id ?? '')
  const [projectKeys, setProjectKeys] = useState<string[]>(initialBinding?.projectKeys ?? [])
  const [classification, setClassification] = useState<Record<string, Cls>>(seedClassification(initialBinding))
  const [reqFields, setReqFields] = useState<string[]>(initialBinding?.requirementContentFields ?? DEFAULT_CONTENT_FIELDS)
  const [defFields, setDefFields] = useState<string[]>(initialBinding?.defectContentFields ?? DEFAULT_CONTENT_FIELDS)
  // Scope filter + automatic sync (#1867).
  const [filterJql, setFilterJql] = useState<string>(initialBinding?.filterJql ?? '')
  const [syncEnabled, setSyncEnabled] = useState<boolean>(initialBinding?.syncEnabled ?? false)
  const [filterValid, setFilterValid] = useState<boolean>(true)
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>(initialBinding?.userDisplayNames ?? {})

  // Discovery (loaded while editing).
  const [projects, setProjects] = useState<IssueSourceProject[] | null>(null)
  const [types, setTypes] = useState<IssueSourceIssueType[] | null>(null)
  const [typesLoading, setTypesLoading] = useState(false)
  const [fields, setFields] = useState<IssueSourceField[] | null>(null)
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)

  // Load projects + fields when the connection changes (form only).
  useEffect(() => {
    if (!editing || !connectionId) return
    let cancelled = false
    setDiscoveryError(null)
    Promise.all([
      applicationsAPI.getIssueSourceProjects(appId, connectionId),
      applicationsAPI.getIssueSourceFields(appId, connectionId),
    ])
      .then(([p, f]) => {
        if (cancelled) return
        setProjects(p)
        setFields(f)
      })
      .catch((err) => {
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err))
      })
    return () => { cancelled = true }
  }, [appId, connectionId, editing])

  // Load the UNION of issue types when the selected projects change, then
  // apply name-based default classifications to any not-yet-classified type.
  useEffect(() => {
    if (!editing || !connectionId || projectKeys.length === 0) {
      setTypes([])
      setTypesLoading(false)
      return
    }
    let cancelled = false
    setTypes(null)
    setTypesLoading(true)
    applicationsAPI
      .getIssueSourceIssueTypes(appId, connectionId, projectKeys)
      .then((t) => {
        if (cancelled) return
        setTypes(t)
        setClassification((prev) => {
          const next = { ...prev }
          for (const type of t) if (next[type.id] === undefined) next[type.id] = defaultClassFor(type.name)
          return next
        })
      })
      .catch((err) => {
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false)
      })
    return () => { cancelled = true }
  }, [appId, connectionId, projectKeys, editing])

  const toggleProject = useCallback((key: string) => {
    setProjectKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }, [])

  // Save only once the current projects' issue types have loaded — clicking
  // before then would persist an empty mapping (defaults are applied when
  // types resolve).
  const canSave = connectionId !== '' && projectKeys.length > 0 && !typesLoading && types !== null && !saving && filterValid

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    setSaveOk(false)
    try {
      // Derive the mapping from the CURRENT union of types only — so a type
      // ID left over in `classification` from a previously-selected project
      // (no longer in the union) is never sent, which would otherwise fail
      // stage-2 validation.
      const currentTypes = types ?? []
      const requirementIssueTypeIds = currentTypes.filter((t) => classification[t.id] === 'requirement').map((t) => t.id)
      const defectIssueTypeIds = currentTypes.filter((t) => classification[t.id] === 'defect').map((t) => t.id)
      // Cache id→name for the mapped types so the summary + a later rename
      // stay legible without a round-trip.
      const issueTypeNames: Record<string, string> = {}
      for (const t of currentTypes) {
        if (classification[t.id] === 'requirement' || classification[t.id] === 'defect') issueTypeNames[t.id] = t.name
      }
      const patch: AppIssueSourceBinding = {
        connectionId,
        projectKeys,
        classificationScheme: 'issueType',
        requirementIssueTypeIds,
        defectIssueTypeIds,
        issueTypeNames,
        requirementContentFields: reqFields,
        defectContentFields: defFields,
        ...(filterJql.trim() ? { filterJql: filterJql.trim() } : {}),
        ...(Object.keys(userDisplayNames).length > 0 ? { userDisplayNames } : {}),
        syncEnabled,
      }
      const res = await applicationsAPI.updateIntegrations(appId, { issueSource: patch })
      // The connection and type mapping decide which issues count as
      // requirements or defects, so the dashboard must ask again.
      void invalidateCoverage(queryClient)
      setBinding(res.bindings.issueSource)
      setSaveOk(true)
      setEditing(false)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save — try again.")
    } finally {
      setSaving(false)
    }
  }

  async function handleUnbind() {
    setSaving(true)
    setSaveError(null)
    try {
      await applicationsAPI.updateIntegrations(appId, { issueSource: null })
      void invalidateCoverage(queryClient)
      setBinding(null)
      setProjectKeys([])
      setClassification({})
      setEditing(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't remove — try again.")
    } finally {
      setSaving(false)
    }
  }

  // ── Empty state: no workspace connection to bind to ──
  if (connections.length === 0) {
    return (
      <CardShell>
        <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
          <strong className="block text-foreground mb-1">No issue tracker is connected on this workspace yet.</strong>
          Connect Jira on the workspace to pull requirements and defects into this app.{' '}
          <Link to={settingsUrl} className="text-primary inline-flex items-center gap-1 hover:underline">
            Open Workspace Settings <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </CardShell>
    )
  }

  const fieldOptions = (fields ?? []).map((f) => ({ value: f.id, label: `${f.name} (${f.id})` }))
  // Mapped requirement + defect type ids — the scope-filter preview's scope.
  const mappedTypeIds = (types ?? [])
    .filter((t) => classification[t.id] === 'requirement' || classification[t.id] === 'defect')
    .map((t) => t.id)

  return (
    <CardShell badge={binding ? <Badge variant="default">Linked</Badge> : <Badge variant="outline">Not linked</Badge>}>
      {saveError && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive" role="alert">{saveError}</div>}
      {saveOk && !editing && <div className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300"><Check className="h-3 w-3" /> Saved</div>}

      {!editing && binding ? (
        <BoundSummary
          binding={binding}
          connection={connections.find((c) => c.id === binding.connectionId)}
          onEdit={() => { setEditing(true); setSaveOk(false) }}
          onUnbind={handleUnbind}
          unbinding={saving}
        />
      ) : (
        <div className="space-y-4">
          {/* Connection */}
          <div className="space-y-1">
            <Label htmlFor="issue-source-connection">Connection</Label>
            {connections.length === 1 ? (
              <div className="text-sm flex items-center gap-2">
                <span className="font-medium">{providerLabel(connections[0]!.provider)}</span>
                <span className="text-muted-foreground">{providerConfigValue(connections[0], 'baseUrl')}</span>
              </div>
            ) : (
              <SearchableSelect
                id="issue-source-connection"
                value={connectionId}
                onChange={(v) => { setConnectionId(v); setProjectKeys([]); setClassification({}); setFilterJql(''); setUserDisplayNames({}) }}
                options={connections.map((c) => ({ value: c.id, label: `${providerLabel(c.provider)} · ${providerConfigValue(c, 'baseUrl')}` }))}
                placeholder="Select a connection"
                aria-label="Issue-tracker connection"
              />
            )}
          </div>

          {/* Projects */}
          <div className="space-y-1.5">
            <Label>Projects</Label>
            {projects === null ? (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading projects…</div>
            ) : projects.length === 0 ? (
              <p className="text-xs text-muted-foreground">No projects are visible with this connection.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {projects.map((p) => {
                  const on = projectKeys.includes(p.key)
                  return (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => toggleProject(p.key)}
                      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs ${on ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}
                      aria-pressed={on}
                    >
                      {on && <Check className="h-3 w-3" />}
                      <span className="font-medium">{p.key}</span>
                      <span className="text-muted-foreground">{p.name}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Pick one or more. Issues from all of them show together.</p>
          </div>

          {/* Type mapping */}
          {projectKeys.length > 0 && (
            <div className="space-y-1.5">
              <Label>Which issue types are requirements vs defects?</Label>
              {types === null ? (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading issue types…</div>
              ) : types.length === 0 ? (
                <p className="text-xs text-muted-foreground">No issue types found in the selected projects.</p>
              ) : (
                <div className="space-y-1.5">
                  {types.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 truncate">{t.name}</span>
                      <ClassPicker value={classification[t.id] ?? 'ignore'} onChange={(c) => setClassification((prev) => ({ ...prev, [t.id]: c }))} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Content fields */}
          {projectKeys.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldPicker label="Requirement fields to import" selected={reqFields} onChange={setReqFields} options={fieldOptions} idPrefix="req" />
              <FieldPicker label="Defect fields to import" selected={defFields} onChange={setDefFields} options={fieldOptions} idPrefix="def" />
            </div>
          )}

          {/* Scope filter + automatic sync (#1867) */}
          {projectKeys.length > 0 && connectionId && (
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
              <ScopeFilterBuilder
                key={connectionId}
                appId={appId}
                connectionId={connectionId}
                projectKeys={projectKeys}
                issueTypeIds={mappedTypeIds}
                value={filterJql}
                onChange={setFilterJql}
                onValidityChange={setFilterValid}
                initialUserNames={userDisplayNames}
                onUserNamesChange={setUserDisplayNames}
              />
              <div className="flex items-center gap-2.5 border-t border-border pt-2.5">
                <button
                  type="button"
                  role="switch"
                  aria-checked={syncEnabled}
                  aria-label="Keep in sync automatically"
                  onClick={() => setSyncEnabled((v) => !v)}
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${syncEnabled ? 'bg-primary' : 'bg-border'}`}
                >
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${syncEnabled ? 'left-[18px]' : 'left-0.5'}`} />
                </button>
                <div>
                  <span className="text-sm font-medium">Keep in sync automatically</span>
                  <p className="text-[11px] text-muted-foreground">Otto checks this tracker on a schedule and refreshes the proposed tests as issues change.</p>
                </div>
              </div>
            </div>
          )}

          {discoveryError && <p className="text-xs text-amber-700 dark:text-amber-300">Couldn’t reach the tracker: {discoveryError}</p>}

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => void handleSave()} disabled={!canSave}>
              {saving ? 'Saving…' : binding ? 'Save changes' : 'Save'}
            </Button>
            {binding && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            )}
          </div>
        </div>
      )}

      {/* Read-only browser — only once bound. */}
      {binding && !editing && <IssueSourceBrowser appId={appId} projectKeys={binding.projectKeys} />}
    </CardShell>
  )
}

// ── Card shell (matches the GitHub/Slack section cards on this tab) ────────
// `badge` is optional — the empty/onboarding state (no workspace tracker to
// link to) shows no status badge, since "Not linked" would mislead when
// there's nothing to link.
function CardShell({ children, badge }: { children: React.ReactNode; badge?: React.ReactNode }) {
  return (
    <section aria-labelledby="issue-source-heading" className="rounded-lg border bg-card p-4">
      <header className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-md text-white text-sm font-extrabold" style={{ background: '#0052cc' }} aria-hidden="true">J</div>
        <h3 id="issue-source-heading" className="text-sm font-semibold flex-1">Issue tracker</h3>
        {badge}
      </header>
      {children}
    </section>
  )
}

// ── 3-way classification picker ────────────────────────────────────────────
function ClassPicker({ value, onChange }: { value: Cls; onChange: (c: Cls) => void }) {
  const opts: Array<{ v: Cls; label: string }> = [
    { v: 'requirement', label: 'Requirement' },
    { v: 'defect', label: 'Defect' },
    { v: 'ignore', label: 'Ignore' },
  ]
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {opts.map((o) => (
        <button
          type="button"
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 text-xs ${value === o.v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
          aria-pressed={value === o.v}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── Content-field chip picker (add via SearchableSelect, remove via chip) ──
function FieldPicker({
  label, selected, onChange, options, idPrefix,
}: {
  label: string
  selected: string[]
  onChange: (next: string[]) => void
  options: Array<{ value: string; label: string }>
  idPrefix: string
}) {
  const labelFor = (id: string) => options.find((o) => o.value === id)?.label ?? id
  const remaining = options.filter((o) => !selected.includes(o.value))
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-field-add`}>{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {selected.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs">
            {labelFor(id)}
            <button type="button" onClick={() => onChange(selected.filter((s) => s !== id))} aria-label={`Remove ${labelFor(id)}`} className="text-muted-foreground hover:text-foreground">×</button>
          </span>
        ))}
      </div>
      <SearchableSelect
        id={`${idPrefix}-field-add`}
        value=""
        onChange={(v) => { if (v) onChange([...selected, v]) }}
        options={remaining}
        placeholder="Add a field…"
        searchPlaceholder="Search fields…"
        aria-label={label}
        disabled={remaining.length === 0}
      />
    </div>
  )
}

// ── Bound summary (compact, when not editing) ──────────────────────────────
function BoundSummary({
  binding, connection, onEdit, onUnbind, unbinding,
}: {
  binding: AppIssueSourceBinding
  connection: AppIssueTrackerConnection | undefined
  onEdit: () => void
  onUnbind: () => void
  unbinding: boolean
}) {
  const names = binding.issueTypeNames ?? {}
  const reqNames = (binding.requirementIssueTypeIds ?? []).map((id) => names[id] ?? id)
  const defNames = (binding.defectIssueTypeIds ?? []).map((id) => names[id] ?? id)
  const baseUrl = providerConfigValue(connection, 'baseUrl')
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Connection</span>
        <span className="font-medium">{providerLabel(connection?.provider)}</span>
        {baseUrl && <span className="text-muted-foreground">· {baseUrl}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <span className="text-muted-foreground text-xs mr-1 self-center">Projects</span>
        {binding.projectKeys.map((k) => (
          <span key={k} className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium">{k}</span>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Requirements:</span> {reqNames.length ? reqNames.join(', ') : '—'}
        {'  ·  '}
        <span className="font-medium text-foreground">Defects:</span> {defNames.length ? defNames.join(', ') : '—'}
      </div>
      {binding.filterJql && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Filter:</span> <span className="font-mono">{binding.filterJql}</span>
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Automatic sync:</span>{' '}
        {binding.syncEnabled
          ? binding.lastSyncedAt
            ? `on · last synced ${relTime(binding.lastSyncedAt)}`
            : 'on · not run yet'
          : 'off'}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onEdit}>Edit</Button>
        <Button size="sm" variant="ghost" onClick={onUnbind} disabled={unbinding} className="text-destructive hover:bg-destructive/10">
          {unbinding ? 'Removing…' : 'Remove'}
        </Button>
      </div>
    </div>
  )
}

// ── Read-only issue browser ────────────────────────────────────────────────
/** Plain-language wording for each attachment-skip reason (#1843 Slice 2b).
 * The backend stores the internal code; the operator reads this. */
const SKIP_REASON_LABELS: Record<string, string> = {
  'no-image-capability': "the selected model can't read images",
  'unsupported-format': 'unsupported file type',
  'no-extractable-text': 'no readable text (scanned or locked PDF)',
  'download-failed': "couldn't download",
  'cap-count': 'too many attachments',
  'cap-bytes': 'attachment too large',
}

/** Group skips by reason → counted, plain-language lines. An unrecognized
 * reason (e.g. a new backend code) falls back to a generic plain phrase —
 * never the raw internal code, so no jargon leaks to the operator. */
function groupSkips(skips: Array<{ filename: string; reason: string }>): Array<{ count: number; label: string }> {
  const counts = new Map<string, number>()
  for (const s of skips) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1)
  return [...counts.entries()].map(([reason, count]) => ({
    count,
    label: SKIP_REASON_LABELS[reason] ?? "couldn't be used",
  }))
}

/** Per-issue import-status pill, with the attachment-skip note (#1843
 * Slice 2b) beneath it when the import couldn't use some attachments. */
function ImportStatusBadge({ s }: { s: IssueImportStatus | undefined }) {
  if (!s) return null
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Queued', cls: 'bg-muted text-muted-foreground' },
    inFlight: { label: 'Importing…', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
    sent: { label: 'Imported', cls: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
    dead: { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' },
  }
  const m = map[s.status] ?? { label: s.status, cls: 'bg-muted text-muted-foreground' }
  const skips = s.attachmentSkips ?? []
  return (
    <div>
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`} title={s.failureReason}>
        {m.label}
      </span>
      {skips.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {groupSkips(skips).map((g, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-amber-700 dark:text-amber-500">
              <span className="h-1 w-1 rounded-full bg-amber-500 shrink-0" aria-hidden="true" />
              {g.count} skipped — {g.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Browse the bound projects' issues and import them as candidates.
 *
 * Exported since #2158 slice 2 so the Proposed tab can host it. Only the
 * IMPORT half moves: the binding itself (which connection, which projects,
 * how issues are classified) stays on the integrations surface, because two
 * places to configure one binding is the duplicate-path failure the review
 * checklist names first. Importing is an action against an existing binding,
 * which is a different thing from defining one.
 *
 * Needs nothing but the app and the bound project keys, which is what makes
 * the split clean.
 */
export function IssueSourceBrowser({ appId, projectKeys }: { appId: string; projectKeys: string[] }) {
  const [filter, setFilter] = useState<string>('') // '' = all bound projects
  const [issues, setIssues] = useState<IssueSourceIssue[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [statuses, setStatuses] = useState<Record<string, IssueImportStatus>>({})
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const load = useCallback(async (reset: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const page = await applicationsAPI.browseIssueSourceIssues(appId, {
        projectKey: filter || undefined,
        cursor: reset ? undefined : cursor,
        limit: 25,
      })
      setIssues((prev) => (reset ? page.issues : [...prev, ...page.issues]))
      setCursor(page.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issues')
    } finally {
      setLoading(false)
    }
  }, [appId, filter, cursor])

  // Reload from the top whenever the project filter changes; clear the
  // selection so "Import selected (N)" never counts now-hidden rows.
  useEffect(() => {
    setSelected(new Set())
    void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const allVisibleSelected = issues.length > 0 && issues.every((i) => selected.has(i.externalId))
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (issues.every((i) => n.has(i.externalId))) for (const i of issues) n.delete(i.externalId)
      else for (const i of issues) n.add(i.externalId)
      return n
    })

  // Poll import status until the queued issues settle (sent/dead) or a cap.
  // First fetch is immediate (snappy feedback), then every 2s. Bail early
  // if no status rows ever materialize (nothing to wait on).
  //
  // An imported issue counts toward coverage once it is sent, so each poll
  // that sees another one sent asks for coverage again, without waiting for
  // the whole batch. One that fails changes no number and is not counted.
  // An import still pending when the polling stops is counted the next time
  // a coverage screen opens, since that read is never treated as fresh.
  const pollStatus = useCallback(async (externalIds: string[]) => {
    let sentSeen = 0
    for (let i = 0; i < 12; i++) {
      if (i > 0) await new Promise((r) => setTimeout(r, 2000))
      let list: IssueImportStatus[]
      try {
        list = await applicationsAPI.getIssueSourceImportStatus(appId, externalIds)
      } catch {
        continue
      }
      setStatuses((prev) => {
        const n = { ...prev }
        for (const s of list) n[s.externalId] = s
        return n
      })
      const sent = list.filter((s) => externalIds.includes(s.externalId) && s.status === 'sent').length
      if (sent > sentSeen) {
        sentSeen = sent
        void invalidateCoverage(queryClient)
      }
      const settled = (id: string) => {
        const s = list.find((x) => x.externalId === id)
        return s?.status === 'sent' || s?.status === 'dead'
      }
      if (externalIds.every(settled)) break
      // No rows appeared after a few tries — stop rather than spin the full
      // window (a rejected/deduped enqueue never produces a row here).
      if (list.length === 0 && i >= 2) break
    }
  }, [appId, queryClient])

  async function handleImport() {
    const toImport = issues.filter((i) => selected.has(i.externalId))
    if (toImport.length === 0) return
    setImporting(true)
    setImportError(null)
    try {
      const req: ImportIssueRequest[] = toImport.map((i) => ({
        externalId: i.externalId, displayKey: i.displayKey, projectId: i.projectId,
        projectKey: i.projectKey, issueType: i.type.id, revisionToken: i.revisionToken,
      }))
      const { results } = await applicationsAPI.importIssueSourceIssues(appId, req)
      setStatuses((prev) => {
        const n = { ...prev }
        for (const r of results) {
          n[r.externalId] = r.result === 'rejected' || r.result === 'enqueue-failed'
            ? { externalId: r.externalId, status: 'dead', failureReason: r.reason, attempts: 0, updatedAt: '' }
            : { externalId: r.externalId, status: 'pending', attempts: 0, updatedAt: '' }
        }
        return n
      })
      setSelected(new Set())
      const queued = results.filter((r) => r.result === 'enqueued' || r.result === 'already-queued').map((r) => r.externalId)
      if (queued.length > 0) void pollStatus(queued)
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to start the import')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="mt-5 border-t pt-4">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issues in this app</h4>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void handleImport()} disabled={importing || selected.size === 0}>
            {importing ? 'Starting…' : `Import selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
          </Button>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter by project"
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All projects</option>
            {projectKeys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button type="button" onClick={() => void load(true)} aria-label="Refresh" className="text-muted-foreground hover:text-foreground"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
      {importError && <p className="text-xs text-destructive" role="alert">{importError}</p>}

      {issues.length === 0 && !loading && !error ? (
        <p className="text-xs text-muted-foreground py-2">No classified issues yet. Map at least one requirement or defect type above to see issues here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-2 w-6">
                  <input type="checkbox" aria-label="Select all issues" checked={allVisibleSelected} onChange={toggleAll} />
                </th>
                <th className="py-1.5 pr-3 font-medium">Key</th>
                <th className="py-1.5 pr-3 font-medium">Summary</th>
                <th className="py-1.5 pr-3 font-medium">Type</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 font-medium">Import</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i) => (
                <tr key={i.externalId} className="border-b border-border/60">
                  <td className="py-1.5 pr-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${i.displayKey}`}
                      checked={selected.has(i.externalId)}
                      onChange={() => toggle(i.externalId)}
                    />
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-primary whitespace-nowrap">{i.displayKey}</td>
                  <td className="py-1.5 pr-3">{i.summary}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{i.type.name}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{i.status}</td>
                  <td className="py-1.5 whitespace-nowrap"><ImportStatusBadge s={statuses[i.externalId]} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 flex items-center gap-3">
        {loading && <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>}
        {cursor && !loading && (
          <Button size="sm" variant="outline" onClick={() => void load(false)}>Load more</Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Imported issues become tests under this app's <strong>Proposed</strong> tab.</p>
    </div>
  )
}
